import type { Expr } from '../core/types';
import { ADD, SUB, MUL, DIV, ND } from './glsl';

/** Band expressions available to an index, keyed by role. */
export type BandExprs = Partial<Record<'red' | 'green' | 'blue' | 'nir' | 'rededge', Expr>>;

export interface IndexDef {
  id: string;
  name: string;
  needs: string[];
  /** Sensible opening display window. */
  range: [number, number];
  /**
   * Physically possible values. A normalized difference cannot leave [-1, 1]; when it
   * does, the denominator has passed through zero — which happens wherever reflectance
   * is near zero, so over water, shadow and any scene carrying a negative offset.
   *
   * Those pixels carry no signal. Left in, they wreck an auto stretch (a p98 of 4.2
   * squeezes real vegetation into a sliver of the ramp) and paint water as though it
   * were the most vigorous crop in the scene.
   */
  bounds?: [number, number];
  ramp: string;
  about: string;
  rgbOnly?: boolean;
  /** Special forms build their expression from state rather than band roles alone. */
  custom?: boolean;
  sam?: boolean;
  calc?: (B: Required<BandExprs>) => Expr;
}

export const INDICES: IndexDef[] = [
  { id: 'ndvi', name: 'NDVI', needs: ['nir', 'red'], range: [-0.1, 0.9], ramp: 'rdylgn',
    bounds: [-1, 1],
    about: 'Normalized difference vegetation index. The default measure of green biomass.',
    calc: (B) => ND(B.nir, B.red) },
  { id: 'ndre', name: 'NDRE', needs: ['nir', 'rededge'], range: [-0.1, 0.6], ramp: 'rdylgn',
    bounds: [-1, 1],
    about: 'Red-edge version of NDVI. Keeps responding after NDVI saturates in a closed canopy.',
    calc: (B) => ND(B.nir, B.rededge) },
  { id: 'gndvi', name: 'GNDVI', needs: ['nir', 'green'], range: [-0.1, 0.8], ramp: 'rdylgn',
    bounds: [-1, 1],
    about: 'Green NDVI — more sensitive to chlorophyll concentration than to biomass.',
    calc: (B) => ND(B.nir, B.green) },
  { id: 'ndwi', name: 'NDWI', needs: ['green', 'nir'], range: [-0.8, 0.5], ramp: 'turbo',
    bounds: [-1, 1],
    about: 'Water index. Positive over open water, negative over vegetation.',
    calc: (B) => ND(B.green, B.nir) },
  { id: 'savi', name: 'SAVI', needs: ['nir', 'red'], range: [-0.1, 0.9], ramp: 'rdylgn',
    bounds: [-1.5, 1.5],
    about: 'Soil-adjusted NDVI (L=0.5). Better over partial canopy where bare soil shows through.',
    calc: (B) => MUL(1.5, DIV(SUB(B.nir, B.red), ADD(ADD(B.nir, B.red), 0.5))) },
  { id: 'osavi', name: 'OSAVI', needs: ['nir', 'red'], range: [-0.1, 0.9], ramp: 'rdylgn',
    bounds: [-1.2, 1.2],
    about: 'Optimized SAVI (L=0.16). Usually the better soil adjustment for row crops.',
    calc: (B) => DIV(SUB(B.nir, B.red), ADD(ADD(B.nir, B.red), 0.16)) },
  { id: 'cire', name: 'CI red edge', needs: ['nir', 'rededge'], range: [0, 4], ramp: 'viridis',
    bounds: [-1, 20],
    about: 'Chlorophyll index. Close to linear against leaf chlorophyll content.',
    calc: (B) => SUB(DIV(B.nir, B.rededge), 1) },
  { id: 'evi', name: 'EVI', needs: ['nir', 'red', 'blue'], range: [-0.1, 1.2], ramp: 'rdylgn',
    bounds: [-1, 1.5],
    about: 'Enhanced vegetation index. Corrects for atmosphere and soil background.',
    calc: (B) => MUL(2.5, DIV(SUB(B.nir, B.red),
      ADD(SUB(ADD(B.nir, MUL(6, B.red)), MUL(7.5, B.blue)), 1))) },
  { id: 'vari', name: 'VARI', needs: ['green', 'red', 'blue'], range: [-0.4, 0.4],
    ramp: 'rdylgn', rgbOnly: true,
    bounds: [-1, 1],
    about: 'RGB-only vegetation index. No NIR needed — for plain colour drone flights.',
    calc: (B) => DIV(SUB(B.green, B.red), SUB(ADD(B.green, B.red), B.blue)) },
  { id: 'gli', name: 'GLI', needs: ['green', 'red', 'blue'], range: [-0.3, 0.5],
    ramp: 'rdylgn', rgbOnly: true,
    bounds: [-1, 1],
    about: 'Green leaf index. RGB-only, more stable than VARI under bright sun.',
    calc: (B) => DIV(SUB(SUB(MUL(2, B.green), B.red), B.blue),
      ADD(ADD(MUL(2, B.green), B.red), B.blue)) },
  { id: 'exg', name: 'ExG', needs: ['green', 'red', 'blue'], range: [-0.2, 0.6],
    ramp: 'rdylgn', rgbOnly: true,
    bounds: [-2, 2],
    about: 'Excess green. RGB-only, the usual choice for separating canopy from soil.',
    calc: (B) => SUB(SUB(MUL(2, B.green), B.red), B.blue) },
  { id: 'sam', name: 'Spectral angle (SAM)', needs: [], range: [0, 25], ramp: 'magma', sam: true,
    about: 'Angle between each pixel\u2019s spectrum and a pinned reference. Invariant to '
      + 'brightness, so shadowed and sunlit ground of the same material still match. '
      + 'Lower is more similar; clip the range to isolate matches.' },
  { id: 'custom', name: 'Custom expression\u2026', needs: [], range: [-1, 1], ramp: 'rdylgn',
    custom: true,
    about: 'Write your own using band role names — nir, red, rededge, swir16 — or b1, b2 for raw bands.' },
];

export const indexById = (id: string): IndexDef | undefined => INDICES.find((i) => i.id === id);
